import { ReactNode } from "react";
import { classNames } from "primereact/utils";
import { SkeletonItem } from "../../../../common/components/skeletons/SkeletonItem";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import { IGiftVoucherModel } from "../models/giftVoucherModels";
import {
  getVoucherDisplayAmounts,
  getVoucherStatusKey,
  getVoucherStatusLabel,
} from "../utils/giftVoucherUtils";
import styles from "./GiftVoucherListItem.module.scss";

interface IGiftVoucherListItemProps {
  voucher?: IGiftVoucherModel;
  actionButtons: ReactNode;
  className?: string;
}

export const GiftVoucherListItem = ({
  voucher,
  actionButtons,
  className,
}: IGiftVoucherListItemProps) => {
  // ItemsList renders placeholder rows with no item while the request is in flight.
  // They keep the card's own frame and layout so the list does not jump on load.
  if (!voucher) {
    return (
      <div className={classNames(styles.root, className)}>
        <div className={styles.root_main}>
          <SkeletonItem isLoading value={null} width={160} />
          <SkeletonItem isLoading value={null} width={220} />
        </div>
        <div className={styles.root_amounts}>
          <SkeletonItem isLoading value={null} width={90} />
        </div>
      </div>
    );
  }

  const statusKey = getVoucherStatusKey(voucher);
  const amounts = getVoucherDisplayAmounts(voucher);

  return (
    <div
      className={classNames(styles.root, className, {
        [styles.root_deleted]: voucher.isDeleted,
      })}
    >
      <div className={styles.root_main}>
        <div className={styles.root_main_title}>
          <span className={styles.root_main_title_name}>{voucher.name}</span>
          <span
            className={classNames(
              styles.root_main_title_badge,
              styles[`root_main_title_badge_${statusKey}`],
            )}
          >
            {getVoucherStatusLabel(voucher)}
          </span>
          <RenderWhen condition={voucher.isDeleted}>
            <span className={styles.root_main_title_deleted}>Изтрит</span>
          </RenderWhen>
        </div>

        <div className={styles.root_main_meta}>
          <RenderWhen condition={!!voucher.soldInOrderNumber}>
            <span>
              Продаден с #{String(voucher.soldInOrderNumber).padStart(4, "0")}
            </span>
          </RenderWhen>
          <RenderWhen condition={!!voucher.holderUserName}>
            <span>{voucher.holderUserName}</span>
          </RenderWhen>
          <span>
            {voucher.validUntil
              ? `Валиден до ${dateUtility.formatDate(voucher.validUntil)}`
              : "Безсрочен"}
          </span>
        </div>
      </div>

      <div className={styles.root_amounts}>
        {/*
          Remaining first and largest: what the voucher is worth RIGHT NOW is the number
          staff act on. The face value is context, shown only once something has been
          spent off it.
        */}
        <span className={styles.root_amounts_remaining}>{amounts.primary}</span>
        <span className={styles.root_amounts_initial}>{amounts.secondary}</span>
      </div>

      {actionButtons}
    </div>
  );
};
